import { Navigate } from "react-router-dom";
import { PortalDashboardShell } from "../components/PortalDashboardShell";
import { PortalTabHeader } from "../components/PortalTabHeader";
import { FirstLoginWelcomePanel } from "../components/FirstLoginWelcomePanel";
import { usePortalAuth } from "../PortalAuthContext";
import { RequestsTab } from "../tabs/RequestsTab";
import { DocumentsTab } from "../tabs/DocumentsTab";
import { DealsTab } from "../tabs/DealsTab";
import { InvoicesTab } from "../tabs/InvoicesTab";
import { BorrowerGoalsTab } from "../tabs/BorrowerGoalsTab";
import { BorrowerVendorsTab } from "../tabs/BorrowerVendorsTab";
import { BorrowerPaymentsTab } from "../tabs/BorrowerPaymentsTab";
import { BuyerPaymentSetupTab } from "../tabs/BuyerPaymentSetupTab";
import { SellerPaymentSetupTab } from "../tabs/SellerPaymentSetupTab";
import { BuyerHomeTab } from "../tabs/BuyerHomeTab";
import { SellerHomeTab } from "../tabs/SellerHomeTab";
import { VendorHomeTab } from "../tabs/VendorHomeTab";
import { TpsHomeTab } from "../tabs/TpsHomeTab";
import { SellerAccountingTab } from "../tabs/SellerAccountingTab";
import { VendorCustomersTab } from "../tabs/VendorCustomersTab";
import { VendorReportsTab } from "../tabs/VendorReportsTab";
import { VendorPerformancePortalTab } from "../tabs/VendorPerformancePortalTab";
import { VendorAuditTab } from "../tabs/VendorAuditTab";
import { FilesTab } from "../tabs/FilesTab";
import { AccountSettingsTab } from "../tabs/AccountSettingsTab";
import { PlaceholderTab } from "../tabs/PlaceholderTab";
import { NotificationsTab } from "../tabs/NotificationsTab";
import { TpsLoansTab } from "../tabs/TpsLoansTab";
import { TpsDebtCollectorLanguageTab } from "../tabs/TpsDebtCollectorLanguageTab";
import { TpsBillsTab } from "../tabs/TpsBillsTab";
import { TpsDistributionLedgerTab } from "../tabs/TpsDistributionLedgerTab";
import { TpsCommissionsTab } from "../tabs/TpsCommissionsTab";
import { TpsCollectionsTab } from "../tabs/TpsCollectionsTab";
import { TpsPaymentSetupTab } from "../tabs/TpsPaymentSetupTab";
import { TpsTaxFormsTab } from "../tabs/TpsTaxFormsTab";
import { RecipientTaxFormsTab } from "../tabs/RecipientTaxFormsTab";
import type { PortalTabId } from "../components/PortalTabMatrix";
import type { MaterialAudience, PortalMeDto } from "../api/types";

// External Portal v2 Phase 2 §4.1 — the per-type recipient shell now
// delegates to PortalDashboardShell, which renders the §4.2 nav matrix
// (Requests + Documents + Account/Settings as real tabs) computed from
// PortalUser.AccountType + Kind. The audience prop continues to gate
// the route by AccountType so a buyer landing on /portal/seller is
// bounced back to their own.

interface RecipientDashboardPageProps {
  audience: Exclude<MaterialAudience, "All" | "Broker">;
}

export function RecipientDashboardPage({ audience }: RecipientDashboardPageProps) {
  const auth = usePortalAuth();

  if (!auth.loading && !auth.me) {
    return <Navigate to="/portal" replace />;
  }
  if (auth.me && auth.me.accountType !== audience) {
    return <Navigate to={dashboardPathFor(auth.me.accountType, auth.me.kind)} replace />;
  }

  // §7 — every tab now leads with the shared PageHeader (eyebrow + heading +
  // subtitle). The one-time first-login welcome panel sits below the header
  // on the Requests (landing) tab only; it is no longer a tab-spanning banner
  // (Round-3 #1 deleted that).
  return (
    <PortalDashboardShell
      pageTitle={`${audience} dashboard`}
      renderTab={(activeTab) => (
        <>
          <PortalTabHeader
            accountType={audience}
            tab={activeTab}
            // The TPS Tax forms tab is the payer's MANAGEMENT surface; the
            // default subtitle reads for the recipient "my forms" view.
            subtitle={
              activeTab === "tax-forms" && audience === "Tps"
                ? "Prepare, approve, and send the 1099 information returns you file for your recipients."
                : activeTab === "payment-setup" && audience === "Tps"
                  ? "Set up your Stripe accounts so Phoenix can route borrower payments to you and pay your servicing fees."
                  : undefined
            }
          />
          {activeTab === "requests" && <FirstLoginWelcomePanel />}
          {renderRecipientTabBody(activeTab, audience)}
        </>
      )}
    />
  );
}

function renderRecipientTabBody(
  activeTab: PortalTabId,
  audience: Exclude<MaterialAudience, "All" | "Broker">,
) {
  switch (activeTab) {
          // Journey-Stage Adaptive Dashboards — the per-type Home dashboard is
          // now the default landing for every external type. Each renders its
          // own stage-adaptive surface.
          case "home":
            if (audience === "Buyer") return <BuyerHomeTab />;
            if (audience === "Seller") return <SellerHomeTab />;
            if (audience === "Vendor") return <VendorHomeTab />;
            if (audience === "Tps") return <TpsHomeTab />;
            return null;
          case "requests":
            return <RequestsTab />;
          case "documents":
            return <DocumentsTab />;
          case "settings":
            return <AccountSettingsTab />;
          // Phase 3 — §4.2 placeholder tabs. No backend wiring; each
          // surface shows "Coming soon" + a one-liner on what it'll
          // do once it ships.
          case "payments":
            // PR-A/PR-B — Buyer's "Payments & ledger" and Seller's income
            // accounting are real (roll-up + drill-in over the servicing
            // ledger). No other recipient type reaches this branch (Broker
            // uses BrokerDashboardPage; Vendor has no payments tab).
            if (audience === "Buyer") return <BorrowerPaymentsTab />;
            if (audience === "Seller") return <SellerAccountingTab />;
            return (
              <PlaceholderTab
                title="Payments & ledger"
                description="Your running balance, scheduled payments, and statements will live here once the payment system is connected."
              />
            );
          // Payment Setup — the guided bank/ACH setup. Buyer connects the bank
          // their loan payments come from; Seller onboards a Stripe payout
          // account. (Broker uses BrokerDashboardPage; Vendor has no setup tab.)
          case "payment-setup":
            // TPS Stripe onboarding (#7b/#9) — the servicer firm's self-serve
            // Stripe Connect setup (its two Connect accounts + on-demand link).
            if (audience === "Tps") return <TpsPaymentSetupTab />;
            if (audience === "Buyer") return <BuyerPaymentSetupTab />;
            if (audience === "Seller") return <SellerPaymentSetupTab />;
            return null;
          case "notifications":
            return <NotificationsTab />;
          case "contributions":
            return (
              <PlaceholderTab
                title="Income & SPV contributions"
                description="Capital contributions, distributions, and per-SPV income will appear here once SPV reporting is wired up."
              />
            );
          case "invoices":
            // Vendor Billing v1.0 — real VFI invoice list + detail (the
            // detail open stamps ViewedAt; payment ships with Stripe).
            return <InvoicesTab />;
          case "deals":
            // External Portal v2 PR 2 (commit D) — read-only mirror
            // of the staff pipeline for deals this party is on.
            return <DealsTab />;
          case "files":
            return <FilesTab />;
          // TPS Servicing Portal PR-C — the servicer's read-only roster of
          // assigned loans, with per-loan accept/decline.
          case "loans":
            return <TpsLoansTab />;
          // TPS Servicing Portal PR-D — the firm's per-state debt-collector
          // disclosure editor (review the primed default, then approve).
          case "debt-collector-language":
            return <TpsDebtCollectorLanguageTab />;
          // TPS Servicing Portal PR-E — the firm's review-and-approve queue for
          // the monthly bills Phoenix prepares on its behalf.
          case "bills":
            return <TpsBillsTab />;
          // TPS Servicing Portal PR-G — the firm's Distribution Ledger (approve
          // each cleared payment's payout) and Commissions (earned servicing-fee
          // income).
          case "distributions":
            return <TpsDistributionLedgerTab />;
          case "commissions":
            return <TpsCommissionsTab />;
          // TPS Servicing Portal PR-H — the firm's default/collection cases
          // (read + add-note + record-recovery, which books the recovery fee).
          case "collections":
            return <TpsCollectionsTab />;
          // TPS Servicing Portal PR-I — the TPS sees the 1099 MANAGEMENT surface
          // (select → prefill → edit → approve → send → e-file); every recipient
          // account type sees a read-only "my forms" surface. The tab id is
          // shared; the component is chosen by account type.
          case "tax-forms":
            return audience === "Tps" ? <TpsTaxFormsTab /> : <RecipientTaxFormsTab />;
          // Vendor Program v2 PR 2 — the monthly cycle.
          case "goals":
            return <BorrowerGoalsTab />;
          case "reports":
            return <VendorReportsTab />;
          case "performance":
            return <VendorPerformancePortalTab />;
          // Annual Vendor Audit — the vendor's copy of the yearly market check
          // on their pricing, plus the two-sided discussion they answer it in.
          // ⚠️ This switch has no `default`, so a missing case renders a live
          // sidebar item with a correct heading and a completely blank body —
          // no error, nothing in a log.
          case "annual-audit":
            return <VendorAuditTab />;
          // Vendor Connection v1 — the explicit two-sided connection.
          case "vendors":
            return <BorrowerVendorsTab />;
          case "customers":
            return <VendorCustomersTab />;
  }
}

/** Resolve the per-type dashboard path for a given AccountType + Kind.
 *  Exported so the redeem / packages-index pages can use the same map. */
export function dashboardPathFor(
  accountType: MaterialAudience,
  kind: "Broker" | "PackageRecipient",
): string {
  if (kind === "Broker") return "/portal/broker";
  switch (accountType) {
    case "Broker":
      return "/portal/broker";
    case "Buyer":
      return "/portal/buyer";
    case "Seller":
      return "/portal/seller";
    case "Vendor":
      return "/portal/vendor";
    case "Tps":
      return "/portal/tps";
    case "LeadGenerator":
      return "/portal/lead-generator";
    case "Brokerage":
      return "/portal/brokerage";
    default:
      return "/portal";
  }
}

/** Resolve the path to a specific TAB of the caller's own dashboard.
 *
 *  A bare dashboard path is NOT the same surface as one of its tabs:
 *  PortalDashboardShell lands on `tabs[0]` (Home for every type) when no
 *  `?tab=` is present, so "the Requests list" is only reachable as
 *  `{dashboard}?tab=requests`. Centralised here because getting that
 *  composition wrong is invisible — the link works, it just arrives
 *  somewhere else.
 *
 *  Null-safe by requirement, not by padding: PackageView builds its content
 *  BEFORE PortalChrome's `auth.me` guard runs, so this is genuinely called
 *  with a null `me` on the first render. The `/portal` fallback mirrors
 *  PortalDetailShell's previous inline behaviour exactly.
 *
 *  A tab that isn't in the caller's lineup (e.g. `requests` for a
 *  LeadGenerator) is deliberately NOT special-cased: PortalDashboardShell's
 *  route guard and LeadGeneratorDashboardPage both already rewrite an
 *  out-of-lineup `?tab=` to the landing tab. */
export function portalTabPathFor(
  me: Pick<PortalMeDto, "accountType" | "kind"> | null | undefined,
  tab: PortalTabId,
): string {
  if (!me) return "/portal";
  const base = dashboardPathFor(me.accountType, me.kind);
  // `All` / unknown falls through to the /portal forwarder, which redirects
  // to dashboardPathFor and drops the query anyway. Return it bare rather
  // than compounding that with a `?tab=` the forwarder cannot carry.
  if (base === "/portal") return "/portal";
  return `${base}?tab=${tab}`;
}
